import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from 'react'

import { cn } from '@/lib/cn'

type Variant = 'ember' | 'outline' | 'ghost' | 'champagne'
type Size = 'sm' | 'md' | 'lg'

const VARIANTS: Record<Variant, string> = {
  // L'accent LeYAMO : la braise. Utilisé pour UNE action par écran.
  // Le reflet est porté par la variante plutôt que par chaque appel : c'est
  // l'action principale du site, elle doit briller de la même façon partout.
  // `min-w-max` : le reflet impose `overflow: hidden`, ce qui autorise le
  // navigateur à écraser le bouton sous la largeur de son libellé dans une
  // rangée serrée — le texte était alors coupé. Les autres variantes ne
  // descendent déjà jamais sous leur libellé. Un bouton dont le libellé passe
  // à la ligne (`whitespace-normal`) ajoute `min-w-0`.
  ember:
    'sheen min-w-max bg-ember text-white shadow-ember hover:brightness-110 hover:-translate-y-0.5 ' +
    'active:translate-y-0 active:brightness-95 border border-transparent',
  outline:
    'border border-line-strong text-content-primary hover:-translate-y-0.5 ' +
    'hover:border-champagne-400 hover:text-champagne-300 hover:shadow-md',
  ghost: 'text-content-secondary hover:text-content-primary',
  champagne:
    'hairline-champagne text-champagne-300 hover:bg-champagne-400/10',
}

const SIZES: Record<Size, string> = {
  // Cibles tactiles : jamais moins de 44 px de haut sur mobile.
  sm: 'h-10 px-4 text-sm',
  md: 'h-12 px-6 text-sm',
  lg: 'h-14 px-8 text-base',
}

const BASE =
  'inline-flex items-center justify-center gap-2 rounded-full font-medium ' +
  'transition duration-[var(--dur-base)] ease-[var(--ease-out)] ' +
  'disabled:pointer-events-none disabled:opacity-50 whitespace-nowrap'

/**
 * Les classes d'un bouton, sans le bouton.
 *
 * Les rubriques sont des pages : leurs liens doivent être des `<Link>` de
 * React Router (navigation sans rechargement, clic milieu, « ouvrir dans un
 * onglet »). Un `<button onClick={navigate}>` perdrait tout cela. On habille
 * donc le lien plutôt que d'imiter le bouton.
 */
export function buttonClasses({
  variant = 'ember',
  size = 'md',
  className,
}: { variant?: Variant; size?: Size; className?: string } = {}) {
  return cn(BASE, VARIANTS[variant], SIZES[size], className)
}

interface CommonProps {
  variant?: Variant
  size?: Size
  className?: string
  children: ReactNode
}

type ButtonProps = CommonProps & ButtonHTMLAttributes<HTMLButtonElement>
type LinkProps = CommonProps & AnchorHTMLAttributes<HTMLAnchorElement>

export function Button({
  variant = 'ember',
  size = 'md',
  type = 'button',
  className,
  children,
  ...rest
}: ButtonProps) {
  // `type` par défaut à « button », et non celui du HTML.
  //
  // Un `<button>` sans type placé dans un `<form>` vaut « submit ». Un bouton
  // de filtre glissé un jour à côté d'un champ de recherche soumettrait donc
  // le formulaire — et rejouerait la création qu'on venait de faire. Le piège
  // ne se voit pas à la relecture : il ne dépend pas du bouton, mais de ce qui
  // l'entoure.
  return (
    <button
      type={type}
      className={cn(BASE, VARIANTS[variant], SIZES[size], className)}
      {...rest}
    >
      {children}
    </button>
  )
}

export function ButtonLink({
  variant = 'ember',
  size = 'md',
  className,
  children,
  ...rest
}: LinkProps) {
  return (
    <a className={cn(BASE, VARIANTS[variant], SIZES[size], className)} {...rest}>
      {children}
    </a>
  )
}
